import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, Provider, Renderer2, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { UserRepo } from 'src/app/repositories/user-repo';
import {DiffusionListPaged, DiffusionListSearchBody, DiffusionListSearchFilter, DiffusionListSearchFilterParams, MatchingType} from 'src/swagger-gen/web-secured/models';
import { FindDiffusionList } from '../../models/find-diffusion-list/find-diffusion-list';

const INPUT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FindDiffusionListComponent),
  multi: true
};


@Component({
    selector: 'app-find-diffusion-list',
    templateUrl: './find-diffusion-list.component.html',
    styleUrls: ['./find-diffusion-list.component.scss'],
    providers: [INPUT_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FindDiffusionListComponent implements OnInit, OnChanges, ControlValueAccessor {

  readonly DELAY_SEARCH_ITEM_AFTER_KEYUP = 500;

  timeoutSearchItem: any;
  itemPlaceholder: string;
  items: FindDiffusionList[];
  itemOffsetList = 0;
  itemLimitList = 15;
  errorGettingItems: boolean;
  nameList = '';
  isInvalid = false;
  notFound = false;

  @Output() listSelected = new EventEmitter<FindDiffusionList>();
  @Input() showText: string;
  @Input() isEditMode: boolean;
  @Input() nameListInvalid: boolean;

  @ViewChild('input', { static: true, read: ElementRef }) _inputRef: ElementRef;

  readonly placeholderUser = 'subscriptions.modal.export.search';
  _onChange = (_: any) => { };
  _onTouched = () => { };

  constructor(private translate: TranslateService,
    private userRepo: UserRepo,
    private _renderer: Renderer2) { }

  writeValue(value: any): void {
    this._renderer.setProperty(this._inputRef.nativeElement, 'value', value);
  }
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }
  onInputChange(): void {
    const value = this._inputRef.nativeElement.value;
    this._onChange(value);
  }

  ngOnInit(): void {
    this.setItemPlaceholder();
    this.nameList = '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.nameList = this.showText;
    this.isInvalid = this.nameListInvalid;
  }

  // USER EVENTS
  onChangeSearchedItem(value: string): void {
    this.nameList = value;

    const findDiffusionList = new FindDiffusionList();
    findDiffusionList.nameList = value;
    this.onClickAddDiffusionListName(findDiffusionList);

    clearTimeout(this.timeoutSearchItem);
    this.timeoutSearchItem = setTimeout(() => {
      if (value !== '') {
        this.searchDiffusionList(value);
      }
    }, this.DELAY_SEARCH_ITEM_AFTER_KEYUP);
  }

  setItemPlaceholder(): void {
    this.itemPlaceholder = this.translate.instant(this.placeholderUser);
  }

  onClickAddDiffusionListName(nameList: FindDiffusionList) {// UserModelDiffList
    this.displayItem(nameList);
    this.notFound = false;
    this.listSelected.emit(nameList);
  }

  searchDiffusionList(value: string) {
    const params = this.getDiffusionListParams();
    const body: DiffusionListSearchBody = this.getDiffusionListBody(value);
    this.userRepo.getDiffusionList(this.itemOffsetList, this.itemLimitList, params, body).toPromise()
      .then((res: DiffusionListPaged) => {
        this.errorGettingItems = false;
        this.afterGetItems(res);
      })
      .catch((error) => {
        this.errorGettingItems = true;
      });
  }

  displayItem(item: FindDiffusionList): string {
    return item.nameList;
  }

  getDiffusionListParams(): any[] {
    return [
      { key: 'sortFieldName', value: 'NAME' },
      { key: 'sortDirection', value: 'ASC' }
    ];
  }

  getDiffusionListBody(value: string): DiffusionListSearchBody {
    const body: DiffusionListSearchBody = {
      filters: []
    };
    const filterName: DiffusionListSearchFilterParams = {
      value: value,
      field: DiffusionListSearchFilter.NAME,
      matchingType: MatchingType.PARTIAL_MATCH_IGNORE_CASE
    };

    body.filters.push(filterName);
    return body;
  }

  afterGetItems(res: DiffusionListPaged): void {
    const list: FindDiffusionList[] = [];
    res?.list.forEach(dfL => {
      const diffusionList: FindDiffusionList = new FindDiffusionList;
      diffusionList.nameList = dfL.name ? dfL.name : '';
      list.push(diffusionList);
    });

    this.items = list;

    if (this.items.length === 0) {
      this.notFound = true;
    } else {
      const exactMatch = this.items.find(item => item.nameList === this.nameList);
      this.notFound = !(!!exactMatch);
    }
  }
}
